/**
 * cart-fee-watcher.js
 * Load globally via theme.liquid.
 *
 * Runs ONLY on the cart page (it returns immediately elsewhere — see the
 * page gate at the top of the IIFE). On the cart page it intercepts fetch
 * calls to cart mutation endpoints, recalculates customisation fees from
 * line item properties × quantity, and patches the hidden fee variants to
 * match.
 *
 * ── Three fee variants ────────────────────────────────────────────────────────
 * Customisation is split into three types, each with its own fee variant
 * (all share the product handle 'embroidery-fee'):
 *
 *   Outline Embroidery     — variant from window.themeFeeVariants.outline
 *   Full Colour Embroidery — variant from window.themeFeeVariants.colour
 *   Print                  — variant from window.themeFeeVariants.print
 *
 * Variant IDs come from theme settings (config/settings_schema.json),
 * emitted to JS via a <script> block in layout/theme.liquid.
 *
 * Each garment line item carries per-layer properties (set by
 * design-placement-block.liquid and read by multi-variant-atc.js too —
 * keep these three files in sync on schema changes):
 *   _Design N type            → 'Outline Embroidery' | 'Full Colour Embroidery' | 'Print'
 *                                (underscore-prefixed: hidden from the customer
 *                                 in cart/checkout but readable here and in the
 *                                 merchant order view)
 *   _Design N area (sq in)    → integer sq in, already clamped to per-type
 *                                minimum
 *
 * The customer-facing 'Design N configuration' property carries the same
 * info as a single readable line ('5.5"x5.0" Outline Embroidery on the
 * Front') for display purposes. This watcher does NOT read the
 * configuration string — it uses the underscore-prefixed atomic fields
 * instead, which are simpler to match against exactly.
 *
 * For each fee variant, expected qty in cart =
 *   Σ over chargeable lines of ( Σ for each Design N of that type: area × line.quantity )
 *
 * Fee lines are hidden from the cart view via CSS
 * (.cart-item[data-url*="embroidery-fee"]) and visible at checkout.
 *
 * Disables +/- buttons during cart updates so rapid clicks can't race
 * the server and leave the fees inconsistent.
 */

(function () {
  'use strict';

  // ── Page gate ─────────────────────────────────────────────────────────────
  //
  // This watcher only has work to do on the cart page: it reconciles the
  // hidden fee-variant line quantities against the customisation properties
  // on the garment lines already in the cart. Everywhere else (product
  // pages, the file-upload page, collection pages) it has nothing to
  // enforce — there are no fee lines to adjust from those contexts.
  //
  // Critically, it must NOT install its fetch/XHR interceptors off the cart
  // page. Those interceptors fire enforceFeeItem() after every cart
  // mutation, and enforceFeeItem() immediately calls /cart.js. On the
  // file-upload page the flow is:
  //   1. multipart POST /cart/add.js  → 200 (file stored)
  //   2. watcher sees the mutation, schedules enforceFeeItem
  //   3. enforceFeeItem fires GET /cart.js  → Cloudflare challenges it (403)
  // The /cart.js in step 3 is a scripted request milliseconds after a POST,
  // which trips Cloudflare's bot challenge. Since the upload page never has
  // fees to enforce, the whole burst is pure waste — gating it away removes
  // the challenge entirely. The design-placement ATC flow navigates to
  // /cart after adding, so enforcement still runs there on arrival.
  //
  // Shopify's cart page is always at the /cart path (optionally with a
  // locale prefix like /en-gb/cart, hence the suffix match rather than
  // strict equality). The cart drawer/notification can appear on any page,
  // but those don't add fee-bearing garment lines — those only come from
  // the design-placement page, which redirects to /cart.
  const _path = window.location.pathname.replace(/\/+$/, '');
  const _onCartPage = /(^|\/)cart$/.test(_path);
  if (!_onCartPage) return;

  // ── Constants ───────────────────────────────────────────────────────────────
  // Type strings must match those written by design-placement-block.liquid
  // and read by multi-variant-atc.js. Keep these three files in sync.
  // The configuration property carries these as substrings (not exact
  // matches), so we use .includes() rather than === when bucketing — see
  // calcExpectedQtys below. The strings are mutually disjoint (none is a
  // substring of another, and none appears in the size or side portions
  // of the configuration value), so includes() is unambiguous.
  const TYPE_OUTLINE = 'Outline Embroidery';
  const TYPE_COLOUR  = 'Full Colour Embroidery';
  const TYPE_PRINT   = 'Print';

  const FEE_HANDLE   = 'embroidery-fee';

  function feeVariants() {
    return window.themeFeeVariants || { outline: 0, colour: 0, print: 0 };
  }

  function feeVariantIdSet() {
    const fv = feeVariants();
    return new Set([fv.outline, fv.colour, fv.print].filter(Boolean));
  }

  function isFeeLine(item) {
    if (item.handle === FEE_HANDLE) return true;
    return feeVariantIdSet().has(item.variant_id);
  }

  let _ownCount  = 0;
  let _enforcing = false;
  let _pendingMutations = 0;

  const QTY_BUTTON_SELECTOR =
    '.cart-quantity .quantity__button, cart-items button[name="minus"], cart-items button[name="plus"]';

  function isOwnRequest() { return _ownCount > 0; }

  async function ownFetch(url, opts) {
    _ownCount++;
    try   { return await fetch(url, opts); }
    finally { _ownCount = Math.max(0, _ownCount - 1); }
  }

  // ── Button locking ─────────────────────────────────────────────────────────

  function lockButtons() {
    document.querySelectorAll(QTY_BUTTON_SELECTOR).forEach(btn => {
      if (!btn.disabled) {
        btn.dataset.mvqLocked = '1';
        btn.disabled = true;
      }
    });
  }

  function unlockButtons() {
    document.querySelectorAll('[data-mvq-locked]').forEach(btn => {
      delete btn.dataset.mvqLocked;
      btn.disabled = false;
    });
  }

  // ── Rate limit handling ────────────────────────────────────────────────────

  let _rateLimited = false;

  function handleRateLimit() {
    if (_rateLimited) return;
    _rateLimited = true;

    document.querySelectorAll(
      QTY_BUTTON_SELECTOR + ', cart-remove-button a, [name="checkout"], .cart__checkout-button'
    ).forEach(el => {
      el.setAttribute('aria-disabled', 'true');
      el.style.pointerEvents = 'none';
      el.style.opacity = '0.4';
    });

    const target = document.querySelector('cart-items .page-width') ||
                   document.querySelector('cart-items') ||
                   document.body;
    if (!document.getElementById('mvq-rate-limit-banner')) {
      const banner = document.createElement('div');
      banner.id = 'mvq-rate-limit-banner';
      banner.setAttribute('role', 'alert');
      banner.style.cssText =
        'background:#fff3cd;border:1px solid #ffc107;color:#664d03;' +
        'padding:1.2rem 1.5rem;margin:1rem 0;border-radius:4px;' +
        'font-size:1.4rem;text-align:center;';
      banner.innerHTML =
        '<strong>Too many cart changes in a short time.</strong><br>' +
        'Please reload the page to continue updating your cart.' +
        ' <button type="button" onclick="window.location.reload()" ' +
        'style="margin-left:1rem;padding:0.4rem 1rem;background:#664d03;' +
        'color:#fff;border:none;border-radius:3px;cursor:pointer;font-size:1.3rem;">' +
        'Reload now</button>';
      target.insertBefore(banner, target.firstChild);
      banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // ── Per-type sq in calculation from per-layer properties ──────────────────
  //
  // For each chargeable (non-fee) line, walk `_Design N type` properties
  // and look up the matching `_Design N area (sq in)`. Both keys are
  // underscore-prefixed so they're hidden from the customer in cart/
  // checkout, but freely readable here and in the merchant order view.
  //
  // Bucket by type, multiply by line quantity, sum across all chargeable
  // lines.

  function calcExpectedQtys(items) {
    const chargeableItems = items.filter(i => !isFeeLine(i));

    let outline = 0;
    let colour  = 0;
    let print   = 0;

    chargeableItems.forEach(item => {
      const props = item.properties || {};
      const qty   = item.quantity;

      Object.keys(props).forEach(key => {
        const m = key.match(/^_Design (\d+) type$/);
        if (!m) return;
        const n       = m[1];
        const type    = props[key];
        const areaRaw = props[`_Design ${n} area (sq in)`];
        if (!areaRaw) return;
        const area = parseInt(String(areaRaw).replace(/[^0-9]/g, ''), 10) || 0;
        if (area <= 0) return;

        const contribution = area * qty;
        if      (type === TYPE_OUTLINE) outline += contribution;
        else if (type === TYPE_COLOUR)  colour  += contribution;
        else if (type === TYPE_PRINT)   print   += contribution;
      });
    });

    return { outline, colour, print };
  }

  // ── Core fee enforcement ───────────────────────────────────────────────────
  //
  // For each fee variant, set the cart line qty to the expected value.
  // The function:
  //   1. Removes duplicate fee lines (keeps first of each variant).
  //   2. Sets the surviving fee line qty to the expected value.
  //   3. Adds a missing fee line via /cart/add.js if needed.

  async function enforceFeeItem(attempt = 1) {
    if (_rateLimited) return;
    if (_enforcing) return;
    _enforcing = true;

    let didWrite = false;
    try {
      const res = await ownFetch('/cart.js');
      if (res.status === 429) { handleRateLimit(); return; }
      if (!res.ok) return;
      const cart = await res.json();

      const totals = calcExpectedQtys(cart.items);
      const fv     = feeVariants();

      // enforceSingleVariant returns true if it issued any cart write.
      if (fv.outline) didWrite = await enforceSingleVariant(cart.items, fv.outline, totals.outline) || didWrite;
      if (fv.colour)  didWrite = await enforceSingleVariant(cart.items, fv.colour,  totals.colour)  || didWrite;
      if (fv.print)   didWrite = await enforceSingleVariant(cart.items, fv.print,   totals.print)   || didWrite;

      updateDisplayedSqIn(totals);

      if (didWrite) {
        // We changed the cart, so our copy is stale — re-read it.
        await refreshFooterTotals();
      } else {
        // Nothing was written, so the cart we read at the top of this function
        // IS current. Apply the footer totals from it rather than spending a
        // second GET /cart.js. That second read was the request most likely to
        // draw a Cloudflare challenge: scripted, and landing moments after the
        // POST that navigated the customer here. It's also the common case now
        // that multi-variant-atc sends the fee lines in the same request as
        // the garments, so there's usually nothing to correct.
        clearLineErrors();
        applyFooterTotalsFromCart(cart);
      }

      // Only re-run a verification pass if this pass actually changed the
      // cart. The second pass existed to catch cases where the server
      // hadn't settled the first pass's writes — but if we made no writes
      // (the common case: the cart was already correct because MVA set the
      // fees when it added the lines), there's nothing to verify and the
      // re-run is pure wasted request volume. Cutting it roughly halves the
      // request burst on cart-page load, keeping us under Shopify's bot-
      // protection threshold that was serving the Cloudflare challenge.
      if (attempt < 2 && didWrite) {
        _enforcing = false;
        setTimeout(() => enforceFeeItem(attempt + 1), 600);
        return;
      }

    } catch (err) {
      console.error('[MVQ] Fee watcher error:', err);
    } finally {
      _enforcing = false;
      // Unlock once we're done — either we've completed a verification pass
      // (attempt >= 2) or the first pass made no changes so no second pass
      // is scheduled (!didWrite).
      if (_pendingMutations <= 0 && (attempt >= 2 || !didWrite)) {
        _pendingMutations = 0;
        unlockButtons();
      }
    }
  }

  // Enforce qty for a single fee variant. Returns true if it issued any
  // cart write (add/change), false if the cart already matched.
  async function enforceSingleVariant(items, variantId, expectedQty) {
    const feeItems = items.filter(i => i.variant_id === variantId);

    // No line, no expected qty — nothing to do.
    if (feeItems.length === 0 && expectedQty === 0) return false;

    // Cart has chargeable lines wanting this fee but no fee line yet — add it.
    if (feeItems.length === 0) {
      const addRes = await ownFetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity: expectedQty }),
      });
      if (!addRes.ok) {
        console.warn('[MVQ] Could not add fee variant', variantId, addRes.status);
      }
      return true;
    }

    let wrote = false;

    // Remove duplicate lines (keep the first).
    for (let i = 1; i < feeItems.length; i++) {
      await ownFetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: feeItems[i].key, quantity: 0 }),
      });
      wrote = true;
    }

    // Adjust qty if needed (zeroes the line if expectedQty === 0).
    const currentQty = feeItems[0].quantity;
    if (currentQty !== expectedQty) {
      await ownFetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: feeItems[0].key, quantity: expectedQty }),
      });
      wrote = true;
    }

    return wrote;
  }

  // ── Display update ─────────────────────────────────────────────────────────

  function updateDisplayedSqIn(totals) {
    document.querySelectorAll('[data-mvq-outline-sqin]').forEach(el => {
      el.textContent = totals.outline + ' sq in';
    });
    document.querySelectorAll('[data-mvq-colour-sqin]').forEach(el => {
      el.textContent = totals.colour + ' sq in';
    });
    document.querySelectorAll('[data-mvq-print-sqin]').forEach(el => {
      el.textContent = totals.print + ' sq in';
    });
  }

  function clearLineErrors() {
    document.querySelectorAll('.cart-item__error-text').forEach(el => { el.textContent = ''; });
    document.querySelectorAll('.cart-item__error').forEach(el => {
      el.classList.remove('cart-item__error--active');
    });
  }

  async function refreshFooterTotals() {
    clearLineErrors();
    try {
      const r = await ownFetch('/cart.js');
      if (!r.ok) return;
      const cart = await r.json();
      applyFooterTotalsFromCart(cart);
    } catch (_) {}
  }

  function applyFooterTotalsFromCart(cart) {
    const { embroideryPence, printPence, oldFeePence } = calcFeeFromItems(cart.items);

    const fmt = pence => window.Shopify?.formatMoney
      ? window.Shopify.formatMoney(pence, '£{{amount}}')
      : '£' + (pence / 100).toFixed(2);

    const embroideryEl = document.querySelector('#main-cart-footer [data-mvq-embroidery-total]');
    if (embroideryEl) embroideryEl.textContent = fmt(embroideryPence);

    const printEl = document.querySelector('#main-cart-footer [data-mvq-print-total]');
    if (printEl) printEl.textContent = fmt(printPence);

    const totalEl = document.querySelector('#main-cart-footer [data-mvq-estimated-total]');
    if (totalEl) {
      const feeTotalPence  = embroideryPence + printPence;
      const correctTotal   = cart.total_price - oldFeePence + feeTotalPence;
      totalEl.textContent  = window.Shopify?.formatMoney
        ? window.Shopify.formatMoney(correctTotal, '£{{amount}} GBP')
        : '£' + (correctTotal / 100).toFixed(2) + ' GBP';
    }
  }

  function initFooterObserver() {
    // No-op: patching happens inline in wrapSectionResponse.
  }

  // ── Click interception ─────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    const btn = e.target.closest(QTY_BUTTON_SELECTOR);
    if (!btn) return;

    if (_rateLimited || _pendingMutations > 0 || _enforcing || btn.disabled) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    document.querySelectorAll(QTY_BUTTON_SELECTOR).forEach(other => {
      if (other !== btn && !other.disabled) {
        other.dataset.mvqLocked = '1';
        other.disabled = true;
      }
    });
    setTimeout(() => {
      if (!btn.disabled) { btn.dataset.mvqLocked = '1'; btn.disabled = true; }
    }, 0);

    setTimeout(() => {
      if (_pendingMutations === 0 && !_enforcing && !_rateLimited) unlockButtons();
    }, 400);
  }, true);

  // ── Section response patcher ───────────────────────────────────────────────
  //
  // Patches the main-cart-footer section HTML that Dawn fetches after mutations,
  // replacing the Embroidery and Print fee totals with values computed from the
  // settled cart state. Both rows are addressed independently.

  function calcFeeFromItems(items) {
    const fv = feeVariants();
    const feeItems = items.filter(i => isFeeLine(i));

    // What the cart currently believes the fee lines cost (may be stale —
    // we subtract this off before adding the corrected total).
    const oldFeePence = feeItems.reduce((s, i) => s + i.final_line_price, 0);

    // Expected sq in totals from the chargeable line properties.
    const totals = calcExpectedQtys(items);

    // Unit prices per fee variant — pulled from the existing fee line if present
    const outlineLine = feeItems.find(i => i.variant_id === fv.outline);
    const colourLine  = feeItems.find(i => i.variant_id === fv.colour);
    const printLine   = feeItems.find(i => i.variant_id === fv.print);

    const outlineUnit = outlineLine ? (outlineLine.price || 0) : 0;
    const colourUnit  = colourLine  ? (colourLine.price  || 0) : 0;
    const printUnit   = printLine   ? (printLine.price   || 0) : 0;

    const embroideryPence = (totals.outline * outlineUnit) + (totals.colour * colourUnit);
    const printPence      = totals.print * printUnit;

    return { embroideryPence, printPence, oldFeePence };
  }

  function patchFooterHtml(html, cartData) {
    if (!cartData || !cartData.items) return html;

    const { embroideryPence, printPence, oldFeePence } = calcFeeFromItems(cartData.items);

    const hasFeeItem = cartData.items.some(isFeeLine);
    if (!hasFeeItem) return html;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const jsContents = doc.querySelector('#main-cart-footer .js-contents');
    if (!jsContents) return html;

    const fmt = pence => window.Shopify?.formatMoney
      ? window.Shopify.formatMoney(pence, '£{{amount}}')
      : '£' + (pence / 100).toFixed(2);

    const embroideryEl = jsContents.querySelector('[data-mvq-embroidery-total]');
    if (embroideryEl) embroideryEl.textContent = fmt(embroideryPence);

    const printEl = jsContents.querySelector('[data-mvq-print-total]');
    if (printEl) printEl.textContent = fmt(printPence);

    const totalEl = jsContents.querySelector('[data-mvq-estimated-total]');
    if (totalEl) {
      const feeTotalPence     = embroideryPence + printPence;
      const correctTotalPence = cartData.total_price - oldFeePence + feeTotalPence;
      totalEl.textContent = window.Shopify?.formatMoney
        ? window.Shopify.formatMoney(correctTotalPence, '£{{amount}} GBP')
        : '£' + (correctTotalPence / 100).toFixed(2) + ' GBP';
    }

    return '<!DOCTYPE html>' + doc.documentElement.outerHTML;
  }

  function wrapSectionResponse(res) {
    return new Response(
      new ReadableStream({
        start(controller) {
          res.text().then(text => {
            try {
              const data = JSON.parse(text);
              const sectionsObj = data['sections'] || data;
              const footerKey = Object.keys(sectionsObj).find(k =>
                k === 'main-cart-footer' || k.endsWith('__cart-footer')
              );
              if (footerKey) {
                sectionsObj[footerKey] = patchFooterHtml(sectionsObj[footerKey], data);
              }
              controller.enqueue(new TextEncoder().encode(JSON.stringify(data)));
            } catch (e) {
              controller.enqueue(new TextEncoder().encode(text));
            }
            controller.close();
          });
        }
      }),
      { status: res.status, statusText: res.statusText, headers: res.headers }
    );
  }

  const SECTION_RE    = /[?&]sections=[^&]*main-cart-footer/;
  const CART_MUTATING = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const _origFetch    = window.fetch.bind(window);

  // ── Fetch interception ─────────────────────────────────────────────────────

  window.fetch = async function (...args) {
    if (isOwnRequest()) return _origFetch(...args);

    const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
    const isMutation = CART_MUTATING.test(url);

    if (isMutation) _pendingMutations++;

    const res = await _origFetch(...args);

    const isExternal = url.startsWith('http') && !url.startsWith(window.location.origin);
    if (res.ok && !isOwnRequest() && !isExternal) {
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const patched = wrapSectionResponse(res);
        if (isMutation) {
          _pendingMutations--;
          // No 429 check here: this branch is guarded by res.ok, which a 429
          // never is. Rate limits are caught by the !res.ok path below.
          if (_pendingMutations <= 0) {
            _pendingMutations = 0;
            setTimeout(() => enforceFeeItem(1), 100);
          }
        }
        return patched;
      }
    }

    if (isMutation) {
      _pendingMutations--;

      if (res.status === 429) { handleRateLimit(); return res; }

      if (_pendingMutations <= 0) {
        _pendingMutations = 0;
        if (res.ok) {
          setTimeout(() => enforceFeeItem(1), 100);
        } else {
          unlockButtons();
        }
      }
    }

    return res;
  };

  // ── XHR interception ──────────────────────────────────────────────────────

  const _origXHRopen = XMLHttpRequest.prototype.open;
  const _origXHRsend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._mvqUrl = url;
    return _origXHRopen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    const url = this._mvqUrl || '';
    const isMutation = CART_MUTATING.test(url);

    if (isMutation && !isOwnRequest()) {
      _pendingMutations++;
      this.addEventListener('loadend', () => {
        _pendingMutations--;
        if (this.status === 429) { handleRateLimit(); return; }
        if (_pendingMutations <= 0) {
          _pendingMutations = 0;
          if (this.status >= 200 && this.status < 300) {
            setTimeout(() => enforceFeeItem(1), 100);
          } else {
            unlockButtons();
          }
        }
      });
    }

    return _origXHRsend.apply(this, arguments);
  };

  document.addEventListener('change', function (e) {
    const input = e.target.closest('.quantity__input, cart-items input[name="updates[]"]');
    if (!input || _rateLimited) return;
    clearTimeout(enforceFeeItem._inputTimer);
    enforceFeeItem._inputTimer = setTimeout(() => enforceFeeItem(1), 600);
  }, true);

  // ── Checkout intercept ────────────────────────────────────────────────────

  async function enforceAndProceed(proceedFn, guardKey, guardTarget) {
    if (guardTarget && guardTarget.dataset[guardKey]) return;
    if (guardTarget) guardTarget.dataset[guardKey] = '1';
    await enforceFeeItem();
    if (guardTarget) delete guardTarget.dataset[guardKey];
    proceedFn();
  }

  document.addEventListener('click', async function (e) {
    const btn = e.target.closest(
      '[name="checkout"], .cart__checkout-button, [href="/checkout"], cart-checkout-button button'
    );
    if (!btn || btn.dataset.mvqChecking) return;
    e.preventDefault();
    e.stopPropagation();
    await enforceAndProceed(() => {
      const form = btn.closest('form');
      if (form) { form.submit(); return; }
      window.location.href = '/checkout';
    }, 'mvqChecking', btn);
  }, true);

  document.addEventListener('submit', async function (e) {
    const form = e.target;
    if (!form) return;
    const action = form.getAttribute('action') || '';
    const id     = form.getAttribute('id') || '';
    if (!action.includes('/cart') && id !== 'cart') return;
    if (form.dataset.mvqSubmitting) return;
    e.preventDefault();
    e.stopPropagation();
    await enforceAndProceed(() => {
      form.dataset.mvqSubmitting = '1';
      form.submit();
      setTimeout(() => delete form.dataset.mvqSubmitting, 500);
    }, 'mvqSubmitting', form);
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    const input = e.target.closest('.quantity__input, cart-items input[name="updates[]"]');
    if (!input || _rateLimited) return;
    clearTimeout(enforceFeeItem._inputTimer);
    enforceFeeItem._inputTimer = setTimeout(() => enforceFeeItem(1), 0);
  }, true);

  // ── Init ──────────────────────────────────────────────────────────────────

  // The first enforcement pass is deliberately delayed. The customer normally
  // arrives here straight off the POST that added their items, and a scripted
  // GET /cart.js landing milliseconds behind that POST is precisely what trips
  // Cloudflare's bot challenge — see the page gate at the top of this file.
  // Nothing on the page is waiting for the pass: the cart is already rendered
  // server-side, and in the common case it's already correct because
  // multi-variant-atc sends the fee lines in the same request as the garments.
  // The delay just puts real distance between the navigation and our first
  // request.
  const INITIAL_ENFORCE_DELAY_MS = 1200;

  // True while multi-variant-atc's two-phase add is mid-flight: phase 1 has
  // landed us on /cart with the first garment line and NO fee lines yet, and
  // phase 2 (mvq-cart-resume.js) is about to add the remaining sizes together
  // with the fee lines. Enforcing now would see fees missing, start writing
  // them itself, and race phase 2 into double-counted fee quantities. Phase 2
  // marks the payload consumed as it submits, so this goes false again as soon
  // as the resume fires.
  function resumePending() {
    try {
      const raw = sessionStorage.getItem('mvq_pending_add');
      if (!raw) return false;
      const payload = JSON.parse(raw);
      return !!payload && payload.v === 1 && !payload.consumed;
    } catch (e) { return false; }
  }

  function startWatcher() {
    initFooterObserver();
    if (resumePending()) return;
    setTimeout(() => enforceFeeItem(1), INITIAL_ENFORCE_DELAY_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWatcher);
  } else {
    startWatcher();
  }

})();
